import apiClient from '../../../lib/api-client';
import { UserAsset, UserAssetFilters, UserAssetStats, ReconciliationReportFilters, ReconciliationReportResponse } from '../types';

const API_BASE = '/user-asset';

export const userAssetService = {
  // Get all user assets with filters
  async getAll(filters: UserAssetFilters = {}): Promise<{
    data: UserAsset[];
    total: number;
    page: number;
    limit: number;
  }> {
    const params = new URLSearchParams();
    
    if (filters.status) params.append('status', filters.status);
    if (filters.asset_type) params.append('asset_type', filters.asset_type);
    if (filters.access_level) params.append('access_level', filters.access_level);
    if (filters.search) params.append('search', filters.search);
    if (filters.user_id) params.append('user_id', filters.user_id);
    if (filters.asset_id) params.append('asset_id', filters.asset_id);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await apiClient.get(`${API_BASE}?${params.toString()}`);
    return response.data.data;
  },

  // Get user's own assets
  async getMyAssets(filters: UserAssetFilters = {}): Promise<{
    data: UserAsset[];
    total: number;
    page: number;
    limit: number;
  }> {
    const params = new URLSearchParams();
    
    if (filters.status) params.append('status', filters.status);
    if (filters.asset_type) params.append('asset_type', filters.asset_type);
    if (filters.access_level) params.append('access_level', filters.access_level);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await apiClient.get(`${API_BASE}/my-assets?${params.toString()}`);
    return response.data.data;
  },

  // Get single user asset
  async getById(id: string): Promise<UserAsset> {
    const response = await apiClient.get(`${API_BASE}/${id}`);
    return response.data.data;
  },

  // Search user assets
  async search(searchParams: {
    query: string;
    filters?: UserAssetFilters;
  }): Promise<{
    data: UserAsset[];
    total: number;
  }> {
    const response = await apiClient.post(`${API_BASE}/search`, {
      query: searchParams.query,
      ...searchParams.filters
    });
    return response.data.data;
  },

  // Download user asset
  async download(id: string): Promise<Blob> {
    const response = await apiClient.get(`${API_BASE}/${id}/download`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Download published user asset
  async downloadPublished(id: string): Promise<Blob> {
    const response = await apiClient.get(`${API_BASE}/${id}/download-published`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Create user asset
  async create(data: Partial<UserAsset>): Promise<UserAsset> {
    const response = await apiClient.post(API_BASE, data);
    return response.data.data;
  },

  // Update user asset. BE (BaseController::update) trả `data` là kết quả save():
  // true khi đã lưu, false khi không tìm thấy tài sản — vẫn với HTTP 200.
  async update(id: string, data: Partial<UserAsset>): Promise<boolean> {
    const response = await apiClient.put(`${API_BASE}/${id}`, data);
    return response.data.data;
  },

  // Delete user asset
  async delete(id: string): Promise<void> {
    await apiClient.delete(`${API_BASE}/${id}`);
  },

  // Revoke user asset
  async revoke(id: string, reason?: string): Promise<UserAsset> {
    const response = await apiClient.post(`${API_BASE}/${id}/revoke`, {
      reason: reason || 'Manually revoked by administrator'
    });
    return response.data.data;
  },

  // Kích hoạt lại tài sản đã bị thu hồi; reason (tùy chọn) được BE lưu vào metadata.restoration
  async reactivate(id: string, reason?: string): Promise<UserAsset> {
    const response = await apiClient.post(`${API_BASE}/${id}/reactivate`, reason ? { reason } : {});
    return response.data.data;
  },

  // Kích hoạt lại nhiều tài sản: gọi từng cái, trả về id thành công/thất bại để báo lại cho người dùng
  async reactivateMultiple(ids: string[], reason?: string): Promise<{ succeeded: string[]; failed: { id: string; message: string; status?: number }[] }> {
    const results = await Promise.allSettled(ids.map(id => this.reactivate(id, reason)));
    const succeeded: string[] = [];
    const failed: { id: string; message: string; status?: number }[] = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        succeeded.push(ids[index]);
      } else {
        failed.push({
          id: ids[index],
          message: result.reason?.response?.data?.message || result.reason?.message || 'Unknown error',
          status: result.reason?.response?.status
        });
      }
    });

    return { succeeded, failed };
  },

  // Revoke multiple user assets
  async revokeMultiple(ids: string[], reason?: string): Promise<UserAsset[]> {
    const promises = ids.map(id => this.revoke(id, reason));
    const results = await Promise.allSettled(promises);
    
    const successful: UserAsset[] = [];
    const failed: { id: string; error: string }[] = [];
    
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        successful.push(result.value);
      } else {
        failed.push({
          id: ids[index],
          error: result.reason?.message || 'Unknown error'
        });
      }
    });
    
    if (failed.length > 0) {
      console.warn('Some revoke operations failed:', failed);
    }
    
    return successful;
  },

  // Get user asset statistics
  async getStats(): Promise<UserAssetStats> {
    const response = await apiClient.get(`${API_BASE}/stats`);
    return response.data.data;
  },

  // Get reconciliation report (aggregated by school and book)
  async getReconciliationReport(filters: ReconciliationReportFilters = {}): Promise<ReconciliationReportResponse> {
    const params = new URLSearchParams();
    if (filters.schoolId) params.append('schoolId', filters.schoolId);
    if (filters.bookId) params.append('bookId', filters.bookId);
    if (filters.status) params.append('status', filters.status);
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);
    if (filters.vat_rate !== undefined) params.append('vat_rate', filters.vat_rate.toString());
    if (filters.month !== undefined) params.append('month', filters.month.toString());
    if (filters.year !== undefined) params.append('year', filters.year.toString());

    const response = await apiClient.get(`${API_BASE}/reconciliation-report?${params.toString()}`);
    return response.data;
  }
};
